import { createContext, useContext, useState, ReactNode } from "react";

// Import all JSON translations
import en from "../locales/en.json";
import hi from "../locales/hi.json";
import or from "../locales/or.json";
import bn from "../locales/bn.json";
import te from "../locales/te.json";
import ta from "../locales/ta.json";
import kn from "../locales/kn.json";
import ml from "../locales/ml.json";
import mr from "../locales/mr.json";
import gu from "../locales/gu.json";
import pa from "../locales/pa.json";

export type LanguageCode = "en" | "hi" | "or" | "bn" | "te" | "ta" | "kn" | "ml" | "mr" | "gu" | "pa";

const TRANSLATIONS: Record<LanguageCode, Record<string, string>> = {
  en, hi, or, bn, te, ta, kn, ml, mr, gu, pa
};

export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "or", label: "ଓଡ଼ିଆ (Odia)" },
  { code: "bn", label: "বাংলা (Bengali)" },
  { code: "te", label: "తెలుగు (Telugu)" },
  { code: "ta", label: "தமிழ் (Tamil)" },
  { code: "kn", label: "ಕನ್ನಡ (Kannada)" },
  { code: "ml", label: "മലയാളം (Malayalam)" },
  { code: "mr", label: "मराठी (Marathi)" },
  { code: "gu", label: "ગુજરાતી (Gujarati)" },
  { code: "pa", label: "ਪੰਜਾਬੀ (Punjabi)" }
] as const;

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem("hospops_lang");
    if (saved && TRANSLATIONS[saved as LanguageCode]) {
      return saved as LanguageCode;
    }
    return "en";
  });

  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
    localStorage.setItem("hospops_lang", lang);
  };

  const t = (key: string): string => {
    const dict = TRANSLATIONS[language];
    if (dict && dict[key]) {
      return dict[key];
    }
    // Fallback to English if translation is missing
    if (TRANSLATIONS["en"][key]) {
      return TRANSLATIONS["en"][key];
    }
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
